<template>
  <v-row
    no-gutters
    justify="end"
    class="message-row"
  >
    <v-col
      cols="12"
      sm="12"
      md="12"
      class="message-col"
    >
      <div
        class="message-outer-wrap"
        :class="{'message-outer-wrap--editing': isEditing}"
      >
        <div class="message-bubble-wrap">
          <v-card
            class="message-bubble user-bubble"
            :class="{'user-bubble--editing': isEditing}"
            flat
          >
            <v-card-text class="message-text">
              <template v-if="isEditing">
                <textarea
                  ref="editTextarea"
                  v-model="editDraft"
                  class="edit-textarea"
                  rows="1"
                  :disabled="streamingActive"
                  @input="resizeEditTextarea"
                  @keydown.esc.prevent="cancelEdit"
                />
              </template>

              <template v-else>
                {{ content }}
              </template>
            </v-card-text>
          </v-card>

          <div
            v-if="isEditing"
            class="edit-actions"
          >
            <button
              type="button"
              class="cancel-button"
              :disabled="streamingActive"
              @click="cancelEdit"
            >
              Cancel
            </button>

            <button
              type="button"
              class="update-button"
              :disabled="!canSubmitEdit"
              @click="submitEdit"
            >
              Update
            </button>
          </div>

          <div
            v-else
            class="message-actions"
          >
            <button
              ref="copyButton"
              type="button"
              class="action-button copy-button"
              :aria-label="copied ? 'Copied message' : 'Copy message'"
              @click="copyMessage"
              @mouseenter="showTooltip('copy')"
              @mouseleave="hideTooltip('copy')"
            >
              <v-icon size="16">
                {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
            </button>

            <button
              v-if="showEditButton"
              ref="editButton"
              type="button"
              class="action-button edit-button"
              aria-label="Edit prompt"
              :disabled="streamingActive"
              @click="editMessage"
              @mouseenter="showTooltip('edit')"
              @mouseleave="hideTooltip('edit')"
            >
              <v-icon size="16">
                mdi-pencil
              </v-icon>
            </button>
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'UserMessage',

  props: {
    message: {
      type    : Object,
      required: true
    },
    showEditButton: {
      type   : Boolean,
      default: false
    },
    streamingActive: {
      type   : Boolean,
      default: false
    },
    isEditing: {
      type   : Boolean,
      default: false
    }
  },

  data () {
    return {
      copied        : false,
      copiedTimer   : null,
      tooltipElement: null,
      editDraft     : ''
    }
  },

  computed: {
    content () {
      return String(this.message?.content || '')
    },

    canSubmitEdit () {
      return !this.streamingActive && this.editDraft.trim().length > 0
    }
  },

  watch: {
    isEditing: {
      immediate: true,
      handler (value) {
        if (value) {
          this.editDraft = this.content
          this.removeTooltip()

          this.$nextTick(() => {
            this.resizeEditTextarea()
            if (this.$refs.editTextarea) {
              this.$refs.editTextarea.focus()
            }
          })

          return
        }

        this.removeTooltip()
      }
    },

    content (value) {
      if (!this.isEditing) {
        this.editDraft = String(value || '')
      }
    }
  },

  beforeDestroy () {
    this.clearCopiedTimer()
    this.removeTooltip()
  },

  methods: {
    showTooltip (type) {
      if (this.isEditing) {
        return
      }

      const button = type === 'copy' ? this.$refs.copyButton : this.$refs.editButton
      const text = type === 'copy'
        ? (this.copied ? 'Copied!' : 'Copy message')
        : 'Edit prompt'

      if (!button) return

      this.removeTooltip()

      const tooltip = document.createElement('div')
      tooltip.className = 'user-message-tooltip'
      tooltip.textContent = text

      const rect = button.getBoundingClientRect()
      const viewportPadding = 8
      const preferredCenterX = rect.left + rect.width / 2

      tooltip.style.cssText = `
        position: fixed;
        top: ${rect.bottom + 4}px;
        left: ${preferredCenterX}px;
        transform: translateX(-50%);
        padding: 3px 8px;
        background: white;
        color: #041f30;
        font-family: 'Inter';
        font-size: 11px;
        font-weight: 400;
        line-height: 21px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        white-space: nowrap;
        z-index: 99999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeIn 0.15s ease forwards;
        visibility: hidden;
      `

      document.body.appendChild(tooltip)

      const halfWidth = tooltip.offsetWidth / 2
      const minCenterX = halfWidth + viewportPadding
      const maxCenterX = window.innerWidth - halfWidth - viewportPadding
      const boundedMaxCenterX = Math.max(minCenterX, maxCenterX)
      const clampedCenterX = Math.min(
        Math.max(preferredCenterX, minCenterX),
        boundedMaxCenterX
      )

      tooltip.style.left = `${clampedCenterX}px`
      tooltip.style.visibility = 'visible'
      this.tooltipElement = tooltip
    },

    hideTooltip () {
      this.removeTooltip()
    },

    removeTooltip () {
      if (this.tooltipElement) {
        this.tooltipElement.remove()
        this.tooltipElement = null
      }
    },

    async copyMessage () {
      if (!this.content) {
        return
      }

      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(this.content)
        this.markCopied()
        return
      }

      this.copyWithExecCommand()
    },

    copyWithExecCommand () {
      const textarea = document.createElement('textarea')
      textarea.value = this.content
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'

      document.body.appendChild(textarea)
      textarea.select()

      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)

      if (copied) {
        this.markCopied()
      }
    },

    markCopied () {
      this.copied = true
      this.clearCopiedTimer()

      if (this.tooltipElement) {
        this.showTooltip('copy')
      }

      this.copiedTimer = setTimeout(() => {
        this.copied = false
        this.copiedTimer = null

        if (this.tooltipElement) {
          this.showTooltip('copy')
        }
      }, 1200)
    },

    clearCopiedTimer () {
      if (!this.copiedTimer) {
        return
      }

      clearTimeout(this.copiedTimer)
      this.copiedTimer = null
    },

    editMessage () {
      if (this.streamingActive) {
        return
      }

      this.$emit('edit-prompt', this.message)
    },

    cancelEdit () {
      this.editDraft = this.content
      this.$emit('cancel-edit-prompt')
    },

    submitEdit () {
      if (!this.canSubmitEdit) {
        return
      }

      this.$emit('submit-edit-prompt', {
        message: this.message,
        content: this.editDraft.trim()
      })
    },

    resizeEditTextarea () {
      const textarea = this.$refs.editTextarea

      if (!textarea) {
        return
      }

      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`
    }
  }
}
</script>

<style scoped>
.message-row {
  display: flex;
  margin: 12px 0;
}

.message-col {
  max-width: 100%;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
}

.message-outer-wrap {
  display: inline-block;
  max-width: 90%;
  min-width: 0;
}

.message-outer-wrap--editing {
  width: 90%;
}

.message-bubble-wrap {
  position: relative;
  padding-bottom: 32px;
  overflow: visible;
}

.message-bubble {
  width: 100%;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .06) !important;
}

.user-bubble {
  background: #CFD9F4 !important;
  border-top-right-radius: 8px;
  box-shadow: none !important;
}

.user-bubble--editing {
  background: #f1f8f1 !important;
  border: 1px solid #9bd8a7;
  border-top-right-radius: 8px;
}

.message-text {
  color: #041F30 !important;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 12px 16px;
}

.edit-textarea {
  width: 100%;
  min-height: 56px;
  max-height: 140px;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  resize: none;
  background: transparent;
  color: #041F30;
  font-family: 'Inter';
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  overflow-y: auto;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.edit-action-button {
  height: 28px;
  min-width: 74px;
  padding: 6px 12px;
  border-radius: 6px;
  color: #041F30;
  font-family: 'Inter';
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.edit-action-button:hover:not(:disabled) {
  background: #ffffff;
  color: #041F30;
}

.update-button {
  padding: 2px 6px;
  background: #ffffff;
  color: #041F30;
  font-family: 'Inter';
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  border-radius: 4px;
  opacity: 1;
}

.update-button:hover {
  border-color: #a9bdd8;
  background: #B9F9BA !important;
  box-shadow: 0 2px 8px rgba(4, 31, 48, 0.12);
  transform: translateY(-1px);
}

.cancel-button {
  padding: 2px 6px;
  color: #041F30 !important;
  font-family: 'Inter';
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  border-radius: 4px;
  opacity: 1;
}

.cancel-button:hover {
  border-color: #a9bdd8;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(4, 31, 48, 0.12);
  transform: translateY(-1px);
}

.edit-action-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.message-actions {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 2px;
  overflow: visible;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.message-bubble-wrap:hover .message-actions {
  opacity: 1;
  pointer-events: auto;
}

.action-button .v-icon {
  color: #1C3880 !important;
}

.action-button:hover .v-icon {
  color: #1C3880 !important;
}

.action-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  min-width: 26px;
  padding: 0;
  border: none;
  background: transparent;
  color: #1C3880 !important;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.15s ease;
  overflow: visible;
  flex-shrink: 0;
}

.action-button:hover {
  color: #1C3880 !important;
  background: #ffffff !important;;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.action-button:active {
  transform: scale(0.95);
}

.action-button:disabled {
  opacity: 0.5;
  box-shadow: none;
  cursor: not-allowed;
}

.action-button:disabled:hover {
  background: transparent;
}
</style>

<style>
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
